import React, { createContext, useContext } from 'react';
import { cn } from '../utils/cn';

interface TabsContextValue {
  value?: string;
  onValueChange?: (value: string) => void;
}

const TabsContext = createContext<TabsContextValue>({});

interface TabsProps {
  value?: string;
  onValueChange?: (value: string) => void;
  defaultValue?: string;
  children: React.ReactNode;
  className?: string;
}

export const Tabs: React.FC<TabsProps> = ({ 
  value,
  onValueChange,
  defaultValue,
  children,
  className
}) => {
  const [internalValue, setInternalValue] = React.useState<string>(defaultValue || '');
  
  const currentValue = value !== undefined ? value : internalValue;
  const handleValueChange = (newValue: string) => {
    if (value === undefined) {
      setInternalValue(newValue);
    }
    onValueChange?.(newValue);
  };

  return (
    <TabsContext.Provider value={{ value: currentValue, onValueChange: handleValueChange }}>
      <div className={className}>
        {children}
      </div>
    </TabsContext.Provider>
  );
};

interface TabsListProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export const TabsList: React.FC<TabsListProps> = ({ 
  children, 
  className,
  ...props 
}) => {
  return (
    <div 
      className={cn("flex gap-5 items-center", className)}
      {...props}
    >
      {children}
    </div>
  );
};

interface TabsTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  value: string;
  badgeCount?: number;
  children: React.ReactNode;
}

export const TabsTrigger: React.FC<TabsTriggerProps> = ({ 
  value,
  badgeCount,
  children,
  className,
  ...props 
}) => {
  const { value: selectedValue, onValueChange } = useContext(TabsContext);
  const isSelected = selectedValue === value;

  return (
    <button
      type="button"
      onClick={() => onValueChange?.(value)}
      className={cn("cursor-pointer", className)}
      {...props}
    >
      <div 
        className={cn(
          "flex gap-1 items-center justify-center pb-3 pt-2",
          isSelected && ""
        )}
      >
        <p className={cn(
          "font-sans font-medium text-base tracking-[-0.16px] whitespace-nowrap",
          isSelected ? "text-text" : "text-text-subtle"
        )}>
          {children}
        </p>
        {badgeCount !== undefined && badgeCount > 0 && (
          <div 
            className="flex items-center justify-center h-5 px-2 rounded-full shrink-0"
            style={{ backgroundColor: 'rgba(37, 37, 139, 0.19)' }}
          >
            <p 
              className="font-sans font-semibold text-xs tracking-[-0.12px] whitespace-nowrap"
              style={{ color: '#000021' }}
            >
              {badgeCount}
            </p>
          </div>
        )}
      </div>
    </button>
  );
};

interface TabsContentProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string;
  children: React.ReactNode;
}

export const TabsContent: React.FC<TabsContentProps> = ({
  value,
  children,
  className,
  ...props
}) => {
  const { value: selectedValue } = useContext(TabsContext);
  const isActive = selectedValue === value;

  return (
    <div className={className} style={isActive ? undefined : { display: 'none' }} {...props}>
      {children}
    </div>
  );
};
